/**
 * List of GraphQL operations that should always use 'no-cache' fetch policy
 */
const NO_CACHE_OPERATIONS = [
    'login',
    'getPaymentProvider',
    'getElavonOneOffSelfCareHpp',
    'getStancerOneOffSelfCareHpp',
];

/**
 * Map of mutations to their dependent queries that should be invalidated
 */
const CACHE_INVALIDATION_MAP = {
    createOrUpdateTvisPin: ['getTvisPin', 'getTvisAccountNumber'],
    editDeviceName: ['getDevices'],
    portExistingCustomer: ['getPortDetails'],
    updatePort: ['getPortDetails'],
    deletePort: ['getPortDetails'],
    getJWTActivationToken: null, // TBU
    validateElavonAddCardSelfCareHpp: ['getPaymentMethod'],
    validateElavonOneOffSelfCareHpp: ['getBalance', 'getBalanceDetails', 'getMyPaymentHistory'],
    validateStancerOneOffSelfCareHpp: ['getBalance', 'getBalanceDetails', 'getMyPaymentHistory'],
    validateStancerAddCardSelfCareHpp: ['getPaymentMethod'],
    deleteCard: ['getPaymentMethod'],
    updateExpiryDate: ['getPaymentMethod'],
    updateDefaultPaymentMethod: ['getPaymentMethod'],
    updatePaymentMethod: ['getPaymentMethod'],
    updateContactNumber: ['getMyContactDetails'],
    updateBillDeliveryType: ['getMyAccountID'],
    updateMyContactPermission: ['getMyPermissionDetails'],
    nddPreference: ['getMyNDDPreference', 'getMyNDDPreference', 'getMyPermissionDetails'],
};

/**
 * NO_CACHE_OPERATION_SET - unique list of graphql queries
 */
const NO_CACHE_OPERATION_SET = new Set(NO_CACHE_OPERATIONS.map(name => name.toLowerCase()));

/**
 * getOperationName - extracts the operation name from a GraphQL query document
 * @param {DocumentNode} query - GraphQL query document
 * @returns {string} - Name of the operation
 */
const getOperationName = query => {
    if (!query?.definitions) {
        return '';
    }
    const operationDefinition = query.definitions.find(
        definition => definition.kind === 'OperationDefinition' && definition.name?.value
    );
    return operationDefinition?.name?.value || '';
};

/**
 * getMutationName - Extracts the mutation name from a GraphQL mutation document
 * @param {DocumentNode} mutation - GraphQL mutation document
 * @returns {string} - Name of the mutation
 */
const getMutationName = mutation => {
    try {
        const definition = mutation.definitions[0];
        if (definition?.selectionSet?.selections[0]) {
            return definition.selectionSet.selections[0]?.name?.value;
        }
    } catch (error) {
        console.error('Error extracting mutation name:', error);
    }
    return null;
};

/**
 * shouldUseNoCache - Determines if a GraphQL operation should use 'no-cache' fetch policy
 * @param {Object} options - Apollo query or mutation options
 * @returns {boolean} - True if the operation should use 'no-cache', false otherwise
 */
const shouldUseNoCache = (options = {}) => {
    const operationName = (
        options.operationName ||
        getOperationName(options.query) ||
        ''
    ).toLowerCase();
    return NO_CACHE_OPERATION_SET.has(operationName);
};

const queryInterceptor = (options = {}) => {
    if (!shouldUseNoCache(options)) {
        return options;
    }

    return {
        ...options,
        fetchPolicy: 'no-cache',
        nextFetchPolicy: 'no-cache',
    };
};

const getMutationNameFromOptions = (options = {}) => {
    if (!options?.mutation) {
        return '';
    }

    return (
        options.mutationNameOverride ||
        getMutationName(options.mutation) ||
        options.operationName ||
        getOperationName(options.mutation) ||
        ''
    );
};

const mutationInterceptor = (client, options = {}) => {
    const mutationName = getMutationNameFromOptions(options);

    if (
        !mutationName ||
        !Object.prototype.hasOwnProperty.call(CACHE_INVALIDATION_MAP, mutationName)
    ) {
        return;
    }

    invalidateCacheForMutation(client, mutationName);
};

/**
 * Invalidates Apollo cache for queries based on the mutation that was executed
 * @param {ApolloClient} client - Apollo Client instance
 * @param {string} mutationName - Name of the mutation that was executed (operationName or fieldName)
 */
const invalidateCacheForMutation = (client, mutationName) => {
    const queriesToInvalidate = CACHE_INVALIDATION_MAP[mutationName];

    if (!queriesToInvalidate || queriesToInvalidate.length === 0) {
        return;
    }
    console.log(
        `Invalidating cache: ${mutationName}, queries impacted: ${queriesToInvalidate.join(', ')}`
    );
    queriesToInvalidate.forEach(queryFieldName => {
        try {
            client.cache.evict({
                id: 'ROOT_QUERY',
                fieldName: queryFieldName,
            });
            console.log(`Successfully invalidated cache for query field: ${queryFieldName}`);
        } catch (error) {
            console.error(`Error invalidating cache for query ${queryFieldName}:`, error);
        }
    });
    client.cache.gc();
};

/**
 * Invalidates Apollo cache for queries based on the operationName provided.
 * @param {ApolloClient} client - Apollo Client instance
 * @param {string} operationName - Name of the operation thats cache needs to be invalidated
 */
const clearCacheQuery = (client, operationName) => {
    console.log(`Invalidating cache: ${operationName}, `);
    try {
        client.cache.evict({
            id: 'ROOT_QUERY',
            fieldName: operationName,
        });
        console.log(`Successfully invalidated cache for query field: ${operationName}`);
    } catch (error) {
        console.error(`Error invalidating cache for query ${operationName}:`, error);
    }
    client.cache.gc();
};

export {
    NO_CACHE_OPERATIONS,
    queryInterceptor,
    CACHE_INVALIDATION_MAP,
    invalidateCacheForMutation,
    mutationInterceptor,
    clearCacheQuery,
};
